import React, { useMemo } from 'react';

import {
  WALLET_AWARD_EMPTY_STATE_TYPES,
  type WalletAwardEmptyStateType,
} from '../../modules/utils/constants/constants';
import {
  EmptyStateCardModel,
  RawEmptyStateCardModel,
  RewardsEarnedData,
} from '../../organisms/WalletAwardsSection/types';
import { useWalletWidgetBadgePublisher } from '../../organisms/WalletWidgetContainer';
import { WalletAwardsEmptyStateProps } from './WalletAwardsEmptyState.types';
import { WalletAwardsEmptyStateView } from './WalletAwardsEmptyStateView';

const getFnaCount = (data?: RewardsEarnedData): number => {
  const certificates = data?.customer?.loyaltyInformation?.certificates;
  return certificates?.total ?? 0;
};

const getNuaCount = (data?: RewardsEarnedData): number => {
  const nua = data?.customer?.loyaltyInformation?.nightlyUpgradeAwards;
  const available = nua?.available;
  if (!available) return 0;

  if (Array.isArray(available)) {
    return available.reduce((sum, award) => sum + (award?.count ?? 0), 0);
  }

  return available.count ?? 0;
};

const normalizeCard = (rawCard: RawEmptyStateCardModel, index: number): EmptyStateCardModel | null => {
  let awardType: WalletAwardEmptyStateType | null = null;

  if (index === 0) {
    awardType = WALLET_AWARD_EMPTY_STATE_TYPES.FNA;
  } else if (index === 1) {
    awardType = WALLET_AWARD_EMPTY_STATE_TYPES.NUA;
  }

  if (!awardType || !rawCard.iconPath || !rawCard.title || !rawCard.description || !rawCard.ctaLabel) return null;

  return {
    awardType,
    iconPath: rawCard.iconPath,
    iconAltText: rawCard.iconAltText,
    title: rawCard.title,
    description: rawCard.description,
    ctaLabel: rawCard.ctaLabel,
    ctaAriaLabel: rawCard.ctaAriaLabel,
    ctaPath: rawCard.ctaPath,
    ctaAction: rawCard.ctaAction,
    ctaModalId: rawCard.ctaModalId,
    openInNewTab: rawCard.openInNewTab,
  };
};

export const WalletAwardsEmptyState: React.FC<WalletAwardsEmptyStateProps> = ({
  componentId,
  hideComponent,
  customClass,
  emptyStateCards,
  dividerBetweenCards,
  rewardsEarnedData,
  isLoading,
}) => {
  const fnaCount = getFnaCount(rewardsEarnedData);
  const nuaCount = getNuaCount(rewardsEarnedData);
  const activeAwardsCount = fnaCount + nuaCount;

  const publishedCount = hideComponent || isLoading ? undefined : String(activeAwardsCount);
  useWalletWidgetBadgePublisher(publishedCount);

  const normalizedCards = useMemo(
    () =>
      (emptyStateCards ?? []).map((card, index) => normalizeCard(card, index)).filter(Boolean) as EmptyStateCardModel[],
    [emptyStateCards]
  );

  const authoredFnaCard = normalizedCards.find(card => card.awardType === WALLET_AWARD_EMPTY_STATE_TYPES.FNA);
  const authoredNuaCard = normalizedCards.find(card => card.awardType === WALLET_AWARD_EMPTY_STATE_TYPES.NUA);

  const fnaCard = fnaCount === 0 ? authoredFnaCard : undefined;
  const nuaCard = nuaCount === 0 ? authoredNuaCard : undefined;
  const shouldShowDivider = dividerBetweenCards !== false;

  return (
    <WalletAwardsEmptyStateView
      componentId={componentId}
      customClass={customClass}
      hideComponent={hideComponent}
      isLoading={isLoading}
      fnaCard={fnaCard}
      nuaCard={nuaCard}
      shouldShowDivider={shouldShowDivider}
      fnaCount={fnaCount}
      nuaCount={nuaCount}
      activeAwardsCount={activeAwardsCount}
    />
  );
};
